import { FC } from 'react'

import { useReactiveVar } from '@apollo/client'

import {
  ManualTransactionsDocument,
  ManualTransactionsQuery,
  OrderPaymentRecordsDocument,
  OrderPaymentRecordsQuery,
} from '__generated__/atlas-types'

import { decimalWithCommas } from 'utils/formatters'

import useWatchQuery from 'hooks/useWatchQuery'
import {
  getPaymentInfo,
  transformPaymentRecords,
} from 'payments/payments.transformations'

import FinancialsViewMode from '../../Review/components/Overview/components/FinancialSummaryWidget/components/FinancialsViewCard'
import { Order } from '../../types'
import { temporaryPendingPayments } from '../store'
import { mergePaymentsWithPendingPayments } from '../utils'
import { getTotalPaidManualTransactions } from './helpers'

interface PropsType {
  order: Order
  brandId: string
  retailerId: string
}

/**
 * Take in account that FinancialSummary has a soft dependency on
 * ManualTransactions and OrderPaymentRecords queries having been
 * called before, as we're only reading from the cache in here.
 */
const FinancialSummary: FC<PropsType> = ({ order, brandId, retailerId }) => {
  const paymentsInPending = useReactiveVar(temporaryPendingPayments)

  const cachedManualTransactions = useWatchQuery<ManualTransactionsQuery>({
    query: ManualTransactionsDocument,
    variables: {
      accountId: brandId,
      orderIds: [order.id],
    },
  })

  const cachedRegularTransactions = useWatchQuery<OrderPaymentRecordsQuery>({
    query: OrderPaymentRecordsDocument,
    variables: {
      orderIds: [order.id],
      accountId: retailerId,
    },
  })

  const totalPaidManualTransaction = getTotalPaidManualTransactions(
    cachedManualTransactions?.manualPaymentRecords,
  )

  const transformedPaymentRecords = transformPaymentRecords(
    cachedRegularTransactions?.paymentRecords,
  )
  const mergedPaymentRecords = mergePaymentsWithPendingPayments({
    queryPaymentRecords: transformedPaymentRecords,
    pendingPaymentRecords: paymentsInPending,
  })

  const paymentData = getPaymentInfo(order.id, mergedPaymentRecords)

  const paymentTotal = totalPaidManualTransaction + (paymentData?.total ?? 0)
  const orderTotal = parseFloat(order?.financials?.dueTotal?.minPrice || '0')
  const remainingAmount =
    orderTotal > paymentTotal ? orderTotal - paymentTotal : 0

  return (
    <FinancialsViewMode
      canViewDiscount={
        Boolean(order?.financials?.orderDiscount) &&
        order?.financials?.orderDiscount?.factor !== '0.00'
      }
      canViewJoorPayPriceModifiers={
        (Boolean(order?.financials?.tax) &&
          order?.financials?.tax?.factor !== '0.00') ||
        Boolean(order?.financials?.customFees?.length)
      }
      currencyCode={order?.currencyCode}
      retailCurrencyCode={order?.priceType?.retailCurrency?.code ?? ''}
      suggestedRetailTotal={order?.financials?.suggestedRetailTotal}
      wholesaleTotal={order?.financials?.wholesaleTotal}
      subTotal={order?.financials?.subTotal}
      grandTotal={order?.financials?.grandTotal}
      dueTotal={order?.financials?.dueTotal}
      orderDiscount={order?.financials?.orderDiscount!}
      shippingFees={order?.financials?.shippingFees!}
      tax={order?.financials?.tax!}
      customFees={order?.financials?.customFees!}
      paidAmount={decimalWithCommas(paymentTotal)}
      remainingBalance={decimalWithCommas(remainingAmount)}
    />
  )
}
export default FinancialSummary
